/**
 * Mapbox Directions API client.
 *
 * Why these request parameters matter:
 *  - `banner_instructions` returns the display banners, and crucially their
 *    `sub` banner carries the per-lane arrow data we draw in the lane bar.
 *  - `steps` + `geometries=geojson` gives us `intersections[].lanes`, the
 *    authoritative lane model (which lanes exist, which are valid for the turn).
 *  - `annotations=congestion` colours the route line by live traffic.
 *  - `bearings` biases the first leg to the direction the vehicle is actually
 *    pointing, which stops a reroute from proposing an immediate U-turn.
 *  - `max_height/width/weight` apply the Hino's dimensions so the route avoids
 *    low bridges and posted weight limits.
 */

import { MAPBOX_TOKEN } from '../core/config';
import { bearing, distance } from '../core/geo';
import { t } from '../core/i18n';
import type { LngLat, DirectionsResult, Route, RouteStep } from '../core/types';
import { activeVehicle, type Settings } from '../core/settings';
import {
  bestBigRoadIndex,
  expresswayGeometryProfile,
  fastRoadAccessProbe,
  FAST_ROAD_EXTRA_DISTANCE_M,
  preferredExpresswayIndex,
  preferredFastRoadIndex,
  roadWidthProfile,
} from './bigRoads';
import { sameRoutePath } from './routeDuplicates';
import { vehicleProfileKey, vehicleViolation } from './vehicleConstraints';
import { reportedExclusions } from './reportedRestrictions';
import { quickestExpresswayIndex } from './expresswayChoice';
import { fastestRouteOrder } from './fastestRoute';
import { applyPace } from './paceModel';
import { roadNumberRefs } from './roads';
import { shouldUseTraffic } from './trafficRegion';

export class DirectionsError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = 'DirectionsError';
  }
}

export interface RouteRequest {
  origin: LngLat;
  destination: LngLat;
  /** Vehicle heading in degrees, used to bias the departure direction. */
  heading?: number | null;
  settings: Settings;
  signal?: AbortSignal;
  /** Extra road classes to exclude for this request only. */
  extraExclude?: string[];
  /**
   * Override the routing profile for this request. Used to fetch a non-traffic
   * line, which often follows the big roads the traffic profile detours off.
   */
  forceProfile?: 'driving' | 'driving-traffic';
  /** Force alternatives on/off for this request. */
  forceAlternatives?: boolean;
  /**
   * Optional intermediate waypoint - used by the fuel detour. Routing THROUGH
   * the station rather than TO it means the trip continues to the original
   * destination on its own once the waypoint is passed, so "return to the
   * normal route when we leave the PTT" needs no extra state to unwind.
   */
  /**
   * Intermediate waypoints. Always a list, never a bare point: `LngLat` is
   * itself a tuple, so `Array.isArray` cannot tell one point from many.
   */
  via?: LngLat[];
  /**
   * Optional travel bearings for silent shaping points. A destination-side
   * probe is discovered while routing in reverse, so its bearing must also be
   * reversed when the forward route is requested or it can snap to the wrong
   * carriageway.
   */
  viaBearings?: Array<number | null>;
  /**
   * Treat `via` coordinates as route-shaping points, not stops. This prevents
   * an artificial expressway continuation point from producing an arrival
   * instruction or splitting the trip into visible legs.
   */
  silentVia?: boolean;
}

/** Human-readable messages for the API's error codes. */
const CODE_MESSAGES: Record<string, string> = {
  NoRoute: 'No drivable route found. The destination may be unreachable for this vehicle.',
  NoSegment: 'Could not find a road near the start or destination. Try moving the pin.',
  ProfileNotFound: 'That routing profile is unavailable.',
  InvalidInput: 'The route request was rejected as invalid.',
  Forbidden: 'Mapbox rejected the token. Check VITE_MAPBOX_TOKEN and its URL restrictions.',
};

function buildExcludes(s: Settings): string[] {
  const ex: string[] = [];
  if (s.route.avoidTolls) ex.push('toll');
  // With a transponder the queue is the cost, not the fee - so keep the toll
  // roads and drop only the plazas we would have to stop and pay cash at.
  else if (activeVehicle(s).easyPass) ex.push('cash_only_tolls');
  if (s.route.avoidMotorways) ex.push('motorway');
  if (s.route.avoidFerries) ex.push('ferry');
  if (s.route.avoidUnpaved) ex.push('unpaved');
  return ex;
}

function buildParams(req: RouteRequest, profile: string): URLSearchParams {
  const { settings } = req;
  const v = activeVehicle(settings);

  const p = new URLSearchParams({
    access_token: MAPBOX_TOKEN,
    geometries: 'geojson',
    overview: 'full',
    steps: 'true',
    banner_instructions: 'true',
    roundabout_exits: 'true',
    // Banner geometry and shields come from Mapbox; Wayfarer composes voice.
    language: settings.language,
    alternatives: String(req.forceAlternatives ?? settings.route.alternatives),
  });

  // Congestion is only produced by the traffic profile; asking for it on plain
  // driving is rejected outright, so gate it.
  p.set(
    'annotations',
    profile === 'driving-traffic'
      ? 'congestion,duration,distance,speed,maxspeed'
      : 'duration,distance,speed,maxspeed',
  );

  const ex = [...new Set([...buildExcludes(settings), ...reportedExclusions(settings), ...(req.extraExclude ?? [])])];
  if (ex.length) p.set('exclude', ex.join(','));

  // Vehicle dimensions. Only send them when they'd actually constrain routing -
  // sending a car's 1.95 m height is harmless but noisy, and for the truck these
  // are the whole point.
  if (v.kind === 'truck') {
    if (!Number.isFinite(v.height) || !(v.height! > 0) || v.height! > 10) {
      throw new DirectionsError('Set the truck height in metres before planning a route.', 'InvalidVehicle');
    }
    if (v.height) p.set('max_height', String(v.height));
    if (v.width) p.set('max_width', String(v.width));
    if (v.weight) p.set('max_weight', String(v.weight));
  }

  // Constrain departure to roughly the current heading (±60°). A silent
  // expressway probe may constrain its own travel direction too; all other
  // waypoints stay empty. The count must match the coordinates exactly.
  const bearingEntries = [
    req.heading != null && Number.isFinite(req.heading) ? `${Math.round(req.heading)},60` : '',
    ...(req.via ?? []).map((_, index) => {
      const viaBearing = req.viaBearings?.[index];
      return viaBearing != null && Number.isFinite(viaBearing)
        ? `${Math.round(viaBearing)},45`
        : '';
    }),
    '',
  ];
  if (bearingEntries.some(Boolean)) {
    p.set('bearings', bearingEntries.join(';'));
  }

  if (req.silentVia && req.via?.length) {
    p.set('waypoints', `0;${req.via.length + 1}`);
    p.set('continue_straight', 'true');
  }

  return p;
}

async function request(req: RouteRequest, profile: string): Promise<DirectionsResult> {
  const { origin, destination } = req;
  const points = [origin, ...(req.via ?? []), destination];
  const coords = points.map((c) => `${c[0].toFixed(6)},${c[1].toFixed(6)}`).join(';');
  const url = `https://api.mapbox.com/directions/v5/mapbox/${profile}/${coords}?${buildParams(req, profile)}`;

  const controller = new AbortController();
  const cancel = () => controller.abort();
  if (req.signal?.aborted) cancel();
  else req.signal?.addEventListener('abort', cancel, { once: true });
  let timedOut = false;
  const deadline = setTimeout(() => { timedOut = true; controller.abort(); }, 8_000);
  try {
    const res = await fetch(url, { signal: controller.signal });
    let body: DirectionsResult & { message?: string };
    try {
      body = await res.json();
    } catch {
      throw new DirectionsError('Routing service returned an unreadable response.', 'BadResponse');
    }

    if (!res.ok || (body.code && body.code !== 'Ok')) {
      const code = body.code ?? (res.status === 401 || res.status === 403 ? 'Forbidden' : 'HttpError');
      throw new DirectionsError(CODE_MESSAGES[code] ?? body.message ?? `Routing failed (${code}).`, code);
    }
    if (!body.routes?.length) {
      throw new DirectionsError(CODE_MESSAGES.NoRoute, 'NoRoute');
    }
    return body;
  } catch (error) {
    if (req.signal?.aborted) throw new DOMException('Route search cancelled', 'AbortError');
    if (timedOut) throw new DirectionsError('Routing service timed out. Please try again.', 'Timeout');
    throw error;
  } finally {
    clearTimeout(deadline);
    req.signal?.removeEventListener('abort', cancel);
  }
}

/**
 * Fetch routes. Tries the live-traffic profile first when enabled and
 * transparently falls back to plain driving, because `driving-traffic` refuses
 * some parameter combinations and is rate-limited more aggressively.
 */
export async function fetchRoutes(req: RouteRequest): Promise<Route[]> {
  const wantTraffic = shouldUseTraffic(
    req.settings.route.trafficMode,
    req.origin,
    req.destination,
  );
  // A caller can pin the profile - the big-roads sweep asks for plain `driving`
  // specifically, because that is the line that follows the trunk roads.
  const profiles = req.forceProfile
    ? [req.forceProfile]
    : wantTraffic
      ? ['driving-traffic', 'driving']
      : ['driving'];

  let lastError: unknown;
  for (const profile of profiles) {
    try {
      const result = await request(req, profile);
      const permitted = result.routes.filter(route => !vehicleViolation(route, req.settings));
      if (!permitted.length) throw new DirectionsError(vehicleViolation(result.routes[0], req.settings) ?? 'No route fits this vehicle.', 'VehicleRestriction');
      return permitted.map((r, i) => ({ ...r, _vehicleProfileKey: vehicleProfileKey(req.settings), _id: `${profile}-${i}-${Date.now()}` }));
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
      lastError = err;
      // A genuine "no route exists" won't be fixed by changing profile.
      if (err instanceof DirectionsError && ['NoRoute', 'Forbidden', 'VehicleRestriction', 'InvalidVehicle'].includes(err.code)) {
        throw err;
      }
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new DirectionsError('Routing failed.', 'Unknown');
}

/**
 * Comparative route search: alongside the default (traffic-aware, with up to
 * two Mapbox alternatives), also query a toll-free and a motorway-free variant
 * in parallel, then dedupe and rank. This is what surfaces the "actually
 * better" choices - e.g. the free road only 4 minutes slower than the tollway.
 */
/**
 * Tag reserved for the widest-roads option. Always present in the result when
 * any route was found at all - see the end of fetchRouteAlternatives().
 */
export const BIG_ROADS_TAG = 'big-roads';

/** Dominant Mapbox road class over a step, matching bigRoads.ts. */
function stepClass(step: RouteStep): string {
  const tally = new Map<string, number>();
  for (const intersection of step.intersections ?? []) {
    const cls = intersection.mapbox_streets_v8?.class;
    if (cls) tally.set(cls, (tally.get(cls) ?? 0) + 1);
  }
  let best = '';
  let count = 0;
  for (const [cls, occurrences] of tally) {
    if (occurrences > count) {
      best = cls;
      count = occurrences;
    }
  }
  return best;
}

interface ArrivalProbe {
  point: LngLat;
  /** Forward travel bearing. The discovery route travelled the other way. */
  bearing: number;
}

/**
 * A point safely onto the first motorway mainline found from the destination.
 * Routing the real journey silently through this point makes Mapbox compare
 * the corresponding destination-side exit instead of only its usual early
 * exit. The bearing is reversed so the point snaps to the forward carriageway.
 */
function arrivalProbeFromReverse(route: Route): ArrivalProbe | null {
  for (const leg of route.legs) {
    for (const step of leg.steps) {
      if (stepClass(step) !== 'motorway') continue;
      const coords = step.geometry?.coordinates;
      if (!coords || coords.length < 2) continue;

      let travelled = 0;
      for (let i = 1; i < coords.length; i++) {
        const segment = distance(coords[i - 1], coords[i]);
        if (!(segment > 0)) continue;
        travelled += segment;
        if (travelled >= 750 || i === coords.length - 1) {
          return {
            point: coords[i],
            bearing: (bearing(coords[i - 1], coords[i]) + 180) % 360,
          };
        }
      }
    }
  }
  return null;
}

export async function fetchRouteAlternatives(
  req: RouteRequest,
  /**
   * Corridors this driver has actually used before, from nav/learnedRoutes.ts.
   * Routing through them surfaces local knowledge the engine cannot infer.
   */
  learned: LngLat[][] = [],
): Promise<Route[]> {
  const { settings } = req;
  const variants: Array<{
    tag?: string;
    kind?: 'unconstrained-access' | 'arrival-discovery';
    promise: Promise<Route[]>;
  }> = [
    { promise: fetchRoutes({ ...req, forceAlternatives: settings.route.alternatives }) },
  ];

  for (const vias of learned.slice(0, 2)) {
    variants.push({
      tag: 'usual',
      promise: fetchRoutes({ ...req, via: vias, forceAlternatives: false }),
    });
  }

  // Always survey the destination side when expressways are allowed. This is
  // internal candidate discovery, not a request to display UI alternatives.
  if (!settings.route.avoidMotorways && !req.via?.length) {
    variants.push({
      kind: 'arrival-discovery',
      promise: fetchRoutes({
        ...req,
        origin: req.destination,
        destination: req.origin,
        heading: null,
        via: undefined,
        viaBearings: undefined,
        silentVia: false,
        forceAlternatives: true,
        forceProfile: 'driving',
      }),
    });
  }

  if (settings.route.alternatives) {
    if (req.heading != null && Number.isFinite(req.heading) && !settings.route.avoidMotorways) {
      variants.push({
        kind: 'unconstrained-access',
        promise: fetchRoutes({ ...req, heading: null, forceAlternatives: true }),
      });
    }
    if (!settings.route.avoidTolls) {
      variants.push({
        tag: 'no-tolls',
        promise: fetchRoutes({ ...req, extraExclude: ['toll'], forceAlternatives: false }),
      });
    }
    if (!settings.route.avoidMotorways) {
      variants.push({
        tag: 'no-motorway',
        promise: fetchRoutes({ ...req, extraExclude: ['motorway'], forceAlternatives: false }),
      });

    }

    /*
     * One extra request purely to widen the pool for the big-roads pick.
     *
     * Mapbox has no "prefer major roads" parameter, so a dual-carriageway
     * route cannot be requested - it can only be recognised among candidates.
     * The plain `driving` profile optimises distance-ish rather than live
     * traffic and routinely returns a different, trunk-road-hugging line that
     * `driving-traffic` never offers. It is one call, and it is what makes the
     * option genuinely available rather than "whichever of the usual three
     * happened to be widest".
     */
    variants.push({
      promise: fetchRoutes({ ...req, forceAlternatives: true, forceProfile: 'driving' }),
    });
  }

  const results = await Promise.allSettled(variants.map((v) => v.promise));
  const routes: Route[] = [];
  results.forEach((r, i) => {
    if (r.status !== 'fulfilled' || variants[i].kind) return;
    for (const route of r.value) {
      routes.push(variants[i].tag ? { ...route, _tag: variants[i].tag } : route);
    }
  });

  /*
   * Compare expressway shapes the ordinary optimum may omit.
   *
   * All choices below are geometric. A traffic ETA can describe a candidate,
   * but it cannot decide whether the initial route abandons the expressway for
   * Bangkok surface streets. Destination-side probes come from the reciprocal
   * survey above, while the access probe still compares a nearby ramp behind
   * the driver's initial heading.
   */
  const primary = results[0];
  const primaryRoutes = primary.status === 'fulfilled' ? primary.value : [];
  const shortestPrimaryDistance = primaryRoutes.reduce(
    (shortest, route) => Math.min(shortest, route.distance),
    Infinity,
  );
  const preferredPrimaryIndex = preferredExpresswayIndex(primaryRoutes);
  const primaryReference =
    preferredPrimaryIndex >= 0
      ? primaryRoutes[preferredPrimaryIndex]
      : primaryRoutes.reduce<Route | null>(
          (shortest, route) => (!shortest || route.distance < shortest.distance ? route : shortest),
          null,
        );

  const unconstrainedAt = variants.findIndex((variant) => variant.kind === 'unconstrained-access');
  const unconstrained = unconstrainedAt >= 0 ? results[unconstrainedAt] : null;
  if (primaryReference && unconstrained?.status === 'fulfilled') {
    const candidates = unconstrained.value.filter(
      (route) => route.distance <= shortestPrimaryDistance + FAST_ROAD_EXTRA_DISTANCE_M,
    );
    const combined = [...primaryRoutes, ...candidates];
    const preferred = preferredExpresswayIndex(combined);
    if (preferred >= primaryRoutes.length) {
      routes.push({ ...combined[preferred], _tag: 'fast-road-access' });
    }
  }

  if (
    primaryReference &&
    !settings.route.avoidMotorways &&
    !req.via?.length &&
    !req.signal?.aborted
  ) {
    const shaped: Array<{
      tag: string;
      kind: 'arrival' | 'access';
      probe: LngLat;
      bearing?: number;
    }> = [];

    const discoveryAt = variants.findIndex((variant) => variant.kind === 'arrival-discovery');
    const discovery = discoveryAt >= 0 ? results[discoveryAt] : null;
    if (discovery?.status === 'fulfilled') {
      const arrivalProbes: ArrivalProbe[] = [];
      const reverseRoutes = discovery.value
        .map((route) => ({ route, geometry: expresswayGeometryProfile(route) }))
        .filter(
          (entry): entry is { route: Route; geometry: NonNullable<typeof entry.geometry> } =>
            entry.geometry != null,
        )
        // Reverse access distance is the forward journey's destination tail.
        .sort(
          (a, b) =>
            a.geometry.disconnectedSurfaceMetres - b.geometry.disconnectedSurfaceMetres ||
            a.geometry.accessMetres - b.geometry.accessMetres ||
            a.geometry.edgeSurfaceMetres - b.geometry.edgeSurfaceMetres ||
            b.geometry.longestConnectedMainlineMetres -
              a.geometry.longestConnectedMainlineMetres ||
            a.route.distance - b.route.distance,
        );
      for (const { route: reverseRoute } of reverseRoutes) {
        const probe = arrivalProbeFromReverse(reverseRoute);
        if (!probe) continue;
        if (arrivalProbes.some((kept) => distance(kept.point, probe.point) < 2_000)) continue;
        arrivalProbes.push(probe);
        if (arrivalProbes.length >= 2) break;
      }
      for (const probe of arrivalProbes) {
        shaped.push({
          tag: 'closest-expressway-exit',
          kind: 'arrival',
          probe: probe.point,
          bearing: probe.bearing,
        });
      }
    }

    const directGeometry = expresswayGeometryProfile(primaryReference);
    const access = fastRoadAccessProbe(req.origin, req.destination);
    if (access && (!directGeometry || directGeometry.accessMetres > 2_000)) {
      shaped.push({ tag: 'fast-road-access', kind: 'access', probe: access });
    }

    const shapedResults = await Promise.allSettled(
      shaped.map((candidate) =>
        fetchRoutes({
          ...req,
          heading: candidate.kind === 'access' ? null : req.heading,
          via: [candidate.probe],
          viaBearings: [candidate.bearing ?? null],
          silentVia: true,
          forceAlternatives: false,
        }),
      ),
    );
    shapedResults.forEach((result, i) => {
      if (result.status !== 'fulfilled') return;
      for (const route of result.value) {
        // A probe may expose a different interchange, but it may not create an
        // arbitrary loop. Expressway presence and preference are both measured
        // from road geometry; live or typical duration is deliberately absent.
        if (route.distance > shortestPrimaryDistance + FAST_ROAD_EXTRA_DISTANCE_M) continue;
        if (!expresswayGeometryProfile(route)) continue;
        routes.push({ ...route, _tag: shaped[i].tag });
      }
    });

    const aborted = shapedResults.some(
      (result) =>
        result.status === 'rejected' &&
        result.reason instanceof DOMException &&
        result.reason.name === 'AbortError',
    );
    if (aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
  }

  if (req.signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  // The primary request failing entirely is a real error worth surfacing.
  if (!routes.length) {
    const first = results[0];
    if (first.status === 'rejected') throw first.reason;
    throw new DirectionsError(CODE_MESSAGES.NoRoute, 'NoRoute');
  }

  // Provider copies can disagree on traffic times and road classifications.
  // Compare the actual traversed geometry in both directions, not vertex sets
  // or a percentage that hides a different ramp on a long shared corridor.
  let unique: Route[] = [];
  for (const route of routes) {
    const duplicate = unique.findIndex(kept => sameRoutePath(kept, route));
    if (duplicate < 0) { unique.push(route); continue; }
    const kept = unique[duplicate];
    const traffic = route._id?.startsWith('driving-traffic-');
    const keptTraffic = kept._id?.startsWith('driving-traffic-');
    if (traffic && !keptTraffic) unique[duplicate] = { ...route, _tag: kept._tag ?? route._tag };
  }

  // Keep surface-road alternatives too: the user now wants the fastest route
  // every time, reversing the former expressway-only candidate filter.
  unique = fastestRouteOrder(applyPace(unique, settings.speeds, settings.signalSeconds), activeVehicle(settings).kind === 'truck');
  for (const route of unique) if (route._tag === 'fastest') route._tag = undefined;
  if (unique.length) unique[0]._tag = 'fastest';
  const profiles = unique.map(roadWidthProfile);

  const bigIdx = bestBigRoadIndex(profiles);
  if (bigIdx >= 0) {
    const big = unique[bigIdx];
    // `_widest` rather than overwriting `_tag`: the widest route is often also
    // the fastest, and replacing the label would hide one fact to show the
    // other. The badge is rendered from this flag, so both can appear.
    big._widest = true;
    if (!big._tag) big._tag = BIG_ROADS_TAG;
  }

  const trimmed = unique.slice(0, 4);
  if (bigIdx >= 0 && !trimmed.includes(unique[bigIdx])) {
    // Drop the slowest un-tagged option to make room rather than growing the
    // list - four is already as many as anyone reads at a junction.
    const sacrifice = [...trimmed].reverse().find((r) => !r._tag);
    const at = sacrifice ? trimmed.indexOf(sacrifice) : trimmed.length - 1;
    trimmed.splice(at, 1, unique[bigIdx]);
  }
  // Preserve the quickest entrance as a selectable alternative, never at the
  // expense of the fastest whole journey in slot A.
  const quickest = quickestExpresswayIndex(unique);
  if (quickest >= 0 && !trimmed.includes(unique[quickest])) {
    let replace = -1;
    for (let i = trimmed.length - 1; i > 0; i--) if (!trimmed[i]._widest) { replace = i; break; }
    if (replace > 0) trimmed[replace] = unique[quickest];
  }
  return fastestRouteOrder(trimmed, activeVehicle(settings).kind === 'truck');
}

/**
 * Reroute pick: get back on the elevated road.
 *
 * "The driving off the elevated motorway should try to go back to the
 * motorway, since that's better and I can live with the result." Same shape as
 * the expressway-first default above, applied to a reroute's candidate pool:
 * prefer genuine motorway mainline whenever the reroute candidate pool has
 * one. Only fall back to the broader fast-road preference when it does not.
 */
export function preferExpresswayRoute(routes: Route[]): Route {
  if (routes.length <= 1) return routes[0];
  const expressway = preferredExpresswayIndex(routes);
  const pick = expressway >= 0 ? expressway : preferredFastRoadIndex(routes);
  return routes[pick >= 0 ? pick : 0];
}

/** Label an alternative route for the route-choice sheet. */
export function summariseRoute(route: Route): string {
  const distanceByRef = new Map<string, { distance: number; first: number }>();
  let order = 0;
  for (const step of route.legs.flatMap((leg) => leg.steps)) {
    for (const ref of roadNumberRefs(step)) {
      const current = distanceByRef.get(ref);
      if (current) current.distance += Math.max(0, step.distance ?? 0);
      else distanceByRef.set(ref, { distance: Math.max(0, step.distance ?? 0), first: order++ });
    }
  }
  const refs = [...distanceByRef.entries()]
    .sort((a, b) => b[1].distance - a[1].distance || a[1].first - b[1].first)
    .slice(0, 2)
    .map(([ref]) => ref);
  return refs.join(' / ') || t('preview.alternative');
}
